import { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { TID } from '../lib/testIds';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PROJECT_TYPES = [
  { value: 'brand_content', label: 'Brand Content' },
  { value: 'weddings', label: 'Weddings' },
  { value: 'sports', label: 'Sports & Athletes' },
  { value: 'events_music', label: 'Events & Music' },
  { value: 'business', label: 'Business' },
  { value: 'realestate', label: 'Real Estate' },
  { value: 'other', label: 'Something else' },
];

// Maps a package card key to a project_type value
export const PACKAGE_TO_TYPE = {
  brand: 'brand_content',
  weddings: 'weddings',
  sports: 'sports',
  events: 'events_music',
  business: 'business',
  realestate: 'realestate',
};

const ContactModalContext = createContext({ open: () => {} });
export const useContactModal = () => useContext(ContactModalContext);

export function ContactModalProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [projectType, setProjectType] = useState('brand_content');

  const open = useCallback((type = 'brand_content') => {
    setProjectType(type);
    setIsOpen(true);
  }, []);

  return (
    <ContactModalContext.Provider value={{ open }}>
      {children}
      <ContactModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        projectType={projectType}
        setProjectType={setProjectType}
      />
    </ContactModalContext.Provider>
  );
}

function ContactModal({ isOpen, setIsOpen, projectType, setProjectType }) {
  const [form, setForm] = useState({ name: '', email: '', budget: '', message: '' });
  const [status, setStatus] = useState('idle');

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in your name, email and message.');
      return;
    }
    setStatus('submitting');
    try {
      await axios.post(`${API}/contact`, { ...form, project_type: projectType });
      setStatus('success');
      toast.success("Inquiry received — we'll be in touch within 48 hours.");
      setForm({ name: '', email: '', budget: '', message: '' });
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === 'string'
          ? detail
          : 'Something went wrong. Email hello@utmedia.com.au directly.'
      );
      setStatus('error');
    }
  };

  const handleOpenChange = (v) => {
    setIsOpen(v);
    if (!v) setTimeout(() => setStatus('idle'), 250);
  };

  const selectedLabel =
    PROJECT_TYPES.find((t) => t.value === projectType)?.label || 'Project';

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid={TID.contactModal}
        className="max-w-xl max-h-[92vh] overflow-y-auto bg-[#0a0a0a] border border-white/15 text-white p-0 gap-0 rounded-xl"
      >
        <div className="p-6 md:p-8 border-b border-white/10">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#D8FF3A] neon-text-glow mb-3 flex items-center gap-2">
            ◆ Start a project
          </p>
          <DialogTitle className="font-display text-2xl md:text-3xl font-bold tracking-tight text-white">
            Enquire — <span className="text-white/50">{selectedLabel}</span>
          </DialogTitle>
        </div>

        {status === 'success' ? (
          <div data-testid={TID.modalSuccess} className="p-8 md:p-10 text-center">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#D8FF3A] mb-3">
              ✓ Inquiry received
            </p>
            <p className="text-white/80 mb-8">
              Thanks — we'll be in touch within 48 hours at the email you provided.
            </p>
            <button
              onClick={() => handleOpenChange(false)}
              className="bg-[#D8FF3A] text-black px-8 py-3.5 text-xs font-bold uppercase tracking-[0.25em] hover:bg-white transition-all"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 md:p-8 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <ModalField label="Your name" required>
                <input
                  data-testid={TID.modalName}
                  type="text"
                  value={form.name}
                  onChange={update('name')}
                  placeholder="Jane Doe"
                  className="modal-input"
                  required
                />
              </ModalField>
              <ModalField label="Email" required>
                <input
                  data-testid={TID.modalEmail}
                  type="email"
                  value={form.email}
                  onChange={update('email')}
                  placeholder="you@studio.com"
                  className="modal-input"
                  required
                />
              </ModalField>
            </div>

            <ModalField label="Project type">
              <div className="flex flex-wrap gap-2" data-testid={TID.modalProjectType}>
                {PROJECT_TYPES.map((t) => (
                  <button
                    type="button"
                    key={t.value}
                    onClick={() => setProjectType(t.value)}
                    className={`px-3 py-2 text-[10px] uppercase tracking-[0.18em] border transition-all ${
                      projectType === t.value
                        ? 'bg-[#D8FF3A] text-black border-[#D8FF3A]'
                        : 'bg-transparent text-white/65 border-white/20 hover:border-[#D8FF3A]/60 hover:text-white'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </ModalField>

            <ModalField label="Budget (optional)">
              <input
                data-testid={TID.modalBudget}
                type="text"
                value={form.budget}
                onChange={update('budget')}
                placeholder="$5,000 – $10,000 AUD"
                className="modal-input"
              />
            </ModalField>

            <ModalField label="Tell us about the project" required>
              <textarea
                data-testid={TID.modalMessage}
                value={form.message}
                onChange={update('message')}
                rows={4}
                placeholder="Vision, timing, references, location…"
                className="modal-input resize-none"
                required
              />
            </ModalField>

            <button
              type="submit"
              data-testid={TID.modalSubmit}
              disabled={status === 'submitting'}
              className="group w-full inline-flex items-center justify-center gap-3 bg-[#D8FF3A] text-black px-8 py-4 text-xs font-bold uppercase tracking-[0.25em] hover:bg-white transition-all disabled:opacity-60 disabled:cursor-wait"
            >
              {status === 'submitting' ? 'Sending…' : 'Send inquiry'}
              <span aria-hidden className="transition-transform duration-500 group-hover:translate-x-1">→</span>
            </button>
            <p className="text-[11px] text-white/40 text-center">
              We reply within 48 hours, weekdays. Or email hello@utmedia.com.au directly.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ModalField({ label, required, children }) {
  return (
    <div>
      <label className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-white/50 mb-2.5">
        <span>{label}</span>
        {required && <span className="text-white/30">· required</span>}
      </label>
      {children}
    </div>
  );
}
